// ═══════════════════════════════════════════════════════════════
// CJTrackingService — Tracking des clics vers produits partenaires
//
// Flow :
//   1. User clique sur "Voir sur iQueens ↗"
//   2. Avant d'ouvrir le browser, on génère un SID unique
//   3. On POST /cj/track-click avec les infos du clic
//   4. On ouvre l'URL avec ?sid={sid} dans les paramètres
//   5. Si achat → CJ webhook POST /cj/webhook avec ce SID
//   6. Backend fait le matching sid → click → conversion
// ═══════════════════════════════════════════════════════════════

import { AWS_CONFIG } from '../config/aws_config';

// Base URL de l'API Lambda
const API = AWS_CONFIG.apiUrl;

// Cache session_id en mémoire (réinitialisé à chaque démarrage app)
let _sessionId = null;

function getSessionId() {
  if (!_sessionId) {
    _sessionId = 's_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }
  return _sessionId;
}

/**
 * Génère un SID unique pour identifier un clic.
 * Format : {userId}_{productId}_{timestamp}_{random}
 * Ce SID est passé dans l'URL CJ et renvoyé dans le webhook lors de la vente.
 */
function generateSid(userId, productId) {
  const uid  = (userId || 'anon').slice(0, 12);
  const pid  = (productId || 'x').slice(0, 20);
  const ts   = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `${uid}_${pid}_${ts}_${rand}`.replace(/[^a-zA-Z0-9_]/g, '');
}

/**
 * Ajoute le SID dans l'URL du produit.
 * CJ accepte le paramètre 'sid' qui sera renvoyé dans le webhook.
 */
function injectSidInUrl(url, sid) {
  if (!url || !sid) return url;
  try {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}sid=${encodeURIComponent(sid)}`;
  } catch {
    return url;
  }
}

/**
 * Tracker un clic sur un produit partenaire.
 * Retourne l'URL enrichie avec le SID à ouvrir dans le browser.
 *
 * @param {Object} product - { id, name, price, originalPrice, category, partner, ... }
 * @param {Object} user    - { id, tier, accessToken }
 * @param {string} screen  - 'home' | 'catalog' | 'looks' | 'wardrobe' | 'travel'
 * @returns {Promise<{sid, trackedUrl, clicked}>}
 */
export async function trackProductClick(product, user, screen = 'unknown', providedSid = null) {
  if (!product) {
    return { sid: null, trackedUrl: null, clicked: false };
  }

  const userId = user?.id || user?.sub || 'anonymous';
  // v1.13.73 — permet au caller de fournir le SID déjà généré (pour que le SID
  // injecté dans l'URL ouverte == le SID enregistré en DB → matching commission).
  const sid = providedSid || generateSid(userId, product.id);

  // URL originale à enrichir
  const originalUrl = product.affiliateUrl || product.originalUrl || product.url;
  const trackedUrl = injectSidInUrl(originalUrl, sid);

  // Payload à envoyer au backend
  const payload = {
    sid,
    user_id: userId,
    product_id: String(product.id || ''),
    product_name: product.name || '',
    product_url: originalUrl || '',
    product_image: product.image || '',
    partner: product.partner || 'unknown',
    category: product.category || '',
    price_eur: Number(product.price) || 0,
    original_price_eur: Number(product.originalPrice) || Number(product.price) || 0,
    discount_pct: Number(product.discount) || 0,
    screen,
    user_tier: user?.tier || 'free',
    session_id: getSessionId(),
  };

  // Fire-and-forget : on n'attend pas la réponse pour ne pas bloquer l'ouverture browser
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (user?.accessToken) headers['Authorization'] = 'Bearer ' + user.accessToken;

    fetch(`${API}/cj/track-click`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }).then(r => {
      if (!r.ok) console.warn('[CJTracking] track-click failed:', r.status);
      else console.log(`[CJTracking] ✓ click tracked: sid=${sid.slice(0, 20)}... partner=${product.partner}`);
    }).catch(e => console.warn('[CJTracking] network error:', e.message));
  } catch (e) {
    console.warn('[CJTracking] tracking error:', e.message);
  }

  return { sid, trackedUrl, clicked: true };
}

/**
 * Récupère le wallet d'un utilisateur.
 * @returns {Promise<Object>} wallet { total_clicks, cashback_pending_eur, cashback_available_eur, ... }
 */
export async function getWallet(user) {
  if (!user?.accessToken) return null;
  try {
    const res = await fetch(`${API}/cj/wallet`, {
      headers: { 'Authorization': 'Bearer ' + user.accessToken },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.warn('[CJTracking] getWallet:', e.message);
    return null;
  }
}

/**
 * Récupère l'historique des conversions d'un user.
 * @param {number} limit - nombre max d'entrées (défaut 50)
 */
export async function getHistory(user, limit = 50) {
  if (!user?.accessToken) return [];
  try {
    const res = await fetch(`${API}/cj/history?limit=${limit}`, {
      headers: { 'Authorization': 'Bearer ' + user.accessToken },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.conversions || [];
  } catch (e) {
    console.warn('[CJTracking] getHistory:', e.message);
    return [];
  }
}

/**
 * Demander un retrait PayPal.
 * @param {Object} user
 * @param {number} amount - montant en €
 * @param {string} paypalEmail
 * @returns {Promise<{success, error, withdrawalId}>}
 */
export async function requestWithdrawal(user, amount, paypalEmail) {
  if (!user?.accessToken) return { success: false, error: 'not_authenticated' };
  try {
    const res = await fetch(`${API}/cj/withdraw`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + user.accessToken,
      },
      body: JSON.stringify({
        amount_eur: amount,
        paypal_email: paypalEmail,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data?.error || 'request_failed' };
    }
    return { success: true, withdrawalId: data?.id };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

export { generateSid, injectSidInUrl };
